import { type ReactNode, type Ref } from 'react';
import Dialog from 'components/shared/Dialog';
import { CloseIcon } from 'components/common/Icons';

import { CloseWrapper, ModalHeader, ModalDescription, ModalFooter } from './index.styled';

// Map size prop to pixel widths
const getModalWidth = (size: string) => {
  switch (size) {
    case 'small':
      return '480px';
    case 'large':
      return '900px';
    case 'extraLarge':
      return '1100px';
    case 'normal':
    default:
      return '700px';
  }
};

type ModalProps = {
  open?: boolean;
  size?: string;
  maxWidth?: string;
  onClose?: () => void;
  children?: ReactNode;
  dataTestId?: string;
  'data-test-id'?: string;
  ref?: Ref<HTMLDivElement>;
};

const Modal = ({ open, size = 'normal', maxWidth, onClose, children, dataTestId, ref, ...props }: ModalProps) => {
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && onClose) {
      onClose();
    }
  };

  // Use maxWidth if provided, otherwise use size-based width
  const modalMaxWidth = maxWidth || getModalWidth(size);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        ref={ref}
        maxWidth={modalMaxWidth}
        data-test-id={dataTestId || 'modal'}
        {...props}
        size="3"
        className="px-4 py-10"
      >
        {onClose && (
          <Dialog.Close>
            <CloseWrapper
              aria-label="Close"
              data-test-id={dataTestId ? `${dataTestId}-close-button` : 'close-modal-button'}
            >
              <CloseIcon aria-hidden />
            </CloseWrapper>
          </Dialog.Close>
        )}

        {children}
      </Dialog.Content>
    </Dialog.Root>
  );
};

export { ModalHeader, ModalDescription, ModalFooter };

export default Modal;
